import React from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const DocumentStatusSection = ({ title, documents, statusType }) => {
  const { t } = useLanguage();

  const getStatusIcon = () => {
    switch (statusType) {
      case 'compliant':
        return { name: 'CheckCircle2', color: 'text-success' };
      case 'expiring':
        return { name: 'Clock', color: 'text-warning' };
      case 'expired':
        return { name: 'AlertCircle', color: 'text-error' };
      default:
        return { name: 'FileText', color: 'text-muted-foreground' };
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      rams: 'H&S Docs',
      insurance: t('insurance'),
      training: t('trainingCertificate'),
      plant: t('plantCertificate')
    };
    return labels?.[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-GB');
  };

  const getDaysRemainingBadge = (days) => {
    if (days === null || days === undefined) return null;
    
    if (days < 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-error/10 text-error rounded text-xs font-medium">
          <Icon name="XCircle" size={12} />
          {t('expired')}
        </span>
      );
    } else if (days <= 14) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-error/10 text-error rounded text-xs font-medium">
          <Icon name="AlertCircle" size={12} />
          {days} {t('days')}
        </span>
      );
    } else if (days <= 30) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded text-xs font-medium">
          <Icon name="AlertTriangle" size={12} />
          {days} {t('days')}
        </span>
      );
    }
    return (
      <span className="text-sm text-muted-foreground">{days} {t('days')}</span>
    );
  };

  const statusIcon = getStatusIcon();

  if (!documents || documents?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-elevation-2 print:shadow-none print:border-2 print:break-inside-avoid">
        <div className="flex items-center gap-3 mb-4">
          <Icon name={statusIcon?.name} size={24} className={statusIcon?.color} />
          <h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
        </div>
        <div className="text-center py-8">
          <Icon name="CheckCircle2" size={48} className="text-success mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{t('noDocumentsInCategory')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 md:p-8 shadow-elevation-2 print:shadow-none print:border-2 print:break-inside-avoid">
      <div className="flex items-center gap-3 mb-6">
        <Icon name={statusIcon?.name} size={24} className={statusIcon?.color} />
        <h2 className="text-xl md:text-2xl font-bold text-foreground">{title}</h2>
        <span className="ml-auto text-sm font-medium text-muted-foreground">
          {documents?.length} {documents?.length === 1 ? t('document') : t('documentsCount')}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('document')}</th>
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('category')}</th>
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('issueDate')}</th>
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('expiryDate')}</th>
              {statusType !== 'compliant' && (
                <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('daysRemaining')}</th>
              )}
              <th className="text-left py-3 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('owner')}</th>
            </tr>
          </thead>
          <tbody>
            {documents?.map((doc, index) => (
              <tr key={doc?.id || index} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                <td className="py-3 px-2">
                  <p className="text-sm font-medium text-foreground">{doc?.title || t('untitled')}</p>
                </td>
                <td className="py-3 px-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs font-medium text-foreground">
                    {getCategoryLabel(doc?.category)}
                  </span>
                </td>
                <td className="py-3 px-2 text-sm text-foreground">
                  {formatDate(doc?.issue_date)}
                </td>
                <td className="py-3 px-2 text-sm text-foreground">
                  {formatDate(doc?.expiry_date)}
                </td>
                {statusType !== 'compliant' && (
                  <td className="py-3 px-2">
                    {getDaysRemainingBadge(doc?.days_until_expiry)}
                  </td>
                )}
                <td className="py-3 px-2 text-sm text-foreground">
                  {doc?.owner || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentStatusSection;